<template>
    <div>
        <div class="participants-grid">
            <PersonCard
                v-for="(personKey, index) in filteredParticipatingPersonsByAgeRestriction"
                :id="'participant-' + personKey"
                :key="personKey"
                :person-key="personKey"
                :index="index"
                :view-only="viewOnly"
                :select-mode="selectMode"
                :mount-opened="personKey === lastRestoredParticipatingPersonKey"
                registering-person-type="participant"
                @delete="deleteParticipant(personKey)"
                @selected-person="$emit('selectedPerson', $event)" />
            <PersonCardWrapper
                v-if="!viewOnly"
                :class="{'pointer': remainingPlacesLeft}"
                @click="add()">
                <div class="d-flex align-items-center justify-content-center flex-column h-100">
                    <h1 class="text-muted">
                        <font-awesome-icon
                            v-if="remainingPlacesLeft"
                            icon="plus"
                            size="lg" />
                        <font-awesome-icon
                            v-else
                            icon="ban"
                            size="lg" />
                    </h1>
                    <h2 class="text-muted mb-0 text-center">
                        <span v-if="selectMode && anyParticipatingPersonsPresent">{{
                            selectModeAddAlternativeLabel
                        }}</span>
                        <span v-else-if="selectMode">{{ selectModeAddLabel }}</span>
                        <span v-else-if="remainingPlacesLeft">
                            <template v-if="changeWordingRegistrationBuy">
                                {{ $t('eventRegistration.participant.addNewPerson') }}
                            </template>
                            <template v-else-if="isCaregiver">
                                {{ $t('eventRegistration.participant.addNewCaregiver') }}
                            </template>
                            <template v-else>
                                {{ $t('eventRegistration.participant.addNewParticipant') }}
                            </template>
                        </span>
                        <span v-else>{{ $t('eventRegistration.participant.noRemainingPlacesLeft') }}</span>
                    </h2>
                </div>
            </PersonCardWrapper>
        </div>
        <base-input
            v-if="!isGroupRegistrar && !selectMode && !viewOnly"
            no-margin
            :model-value="participatingPersons.length"
            mode="lazy"
            name="participantLogicCheck"
            :rules="{
                participantsRequired: !changeWordingRegistrationBuy && !isCaregiver,
                caregiversRequired: !changeWordingRegistrationBuy && isCaregiver,
                personsRequired: changeWordingRegistrationBuy
            }">
            <input class="d-none">
        </base-input>
        <div
            v-if="!(selectMode || viewOnly)"
            class="participants-deleted-grid mt-5">
            <PersonCard
                v-for="(personKey, index) in deletedParticipatingPersons"
                :id="'participant-' + personKey"
                :key="personKey"
                :person-key="personKey"
                :index="index"
                :deleted="true"
                registering-person-type="participant"
                @restore="restoreParticipant(personKey)" />
        </div>
    </div>
</template>

<script>
import PersonCard from "@/components/pages/registration/persons/PersonCard";
import PersonCardWrapper from "@/components/pages/registration/persons/PersonCardWrapper";
import mainRegistrationMixin from "@/mixins/registration/mainRegistrationMixin";
import registrationGroupMixin from "@/mixins/registration/registrationGroupMixin";
import registrationOnlineRegistrationFieldsMixin from "@/mixins/registration/registrationOnlineRegistrationFieldsMixin";
import registrationCaregiverMixin from "@/mixins/registration/registrationCaregiverMixin";
import registrationPersonsMixin from "@/mixins/registration/registrationPersonsMixin";

export default {
    name: "ParticipantsListing",
    components: {PersonCardWrapper, PersonCard},
    mixins: [mainRegistrationMixin, registrationGroupMixin, registrationCaregiverMixin, registrationOnlineRegistrationFieldsMixin, registrationPersonsMixin],
    props: {
        selectMode: {
            type: Boolean,
            default: false
        },
        selectModeAddLabel: {
            type: String
        },
        selectModeAddAlternativeLabel: {
            type: String
        },
        filterByAge: {
            type: Boolean,
            default: false
        },
        viewOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            lastRestoredParticipatingPersonKey: null
        }
    },
    computed: {
        deletedParticipatingPersons: {
            get: function () {
                return this.registrationData.deletedParticipatingPersons
            },
            set: function (value) {
                this.setRegistrationProp('deletedParticipatingPersons', value)
            }
        },
        filteredParticipatingPersonsByAgeRestriction() {
            if (!this.filterByAge || this.getOrfValue("birthday", "invoiceRecipient") !== 2) return this.participatingPersons
            return this.participatingPersons.filter((personKey) => {
                const birthday = this.registrationData.persons[personKey]?.birthday
                if (!birthday) return true
                return this.$moment(birthday).isSameOrBefore(this.$moment().subtract((this.invoiceRecipient1417 ? 14 : 18), 'years'))
            })
        },
        anyParticipatingPersonsPresent() {
            return !!this.filteredParticipatingPersonsByAgeRestriction?.length
        }
    },
    methods: {
        add() {
            if (this.selectMode) {
                this.$emit('add')
            } else {
                this.addParticipant()
            }
        },
        deleteParticipant(participatingPersonKey) {
            this.participatingPersons = this.participatingPersons.filter(
                (participatingPerson) => participatingPerson !== participatingPersonKey
            )

            const deletedParticipatingPersons = this.$cloneDeep(this.deletedParticipatingPersons)
            deletedParticipatingPersons.push(participatingPersonKey)
            this.deletedParticipatingPersons = deletedParticipatingPersons
        },
        restoreParticipant(participatingPersonKey) {
            if (!this.remainingPlacesLeft) {
                this.$notify({message: this.$t('eventRegistration.participant.canNotRestore'), type: 'danger'})
                return;
            }
            this.deletedParticipatingPersons = this.deletedParticipatingPersons.filter(
                (deletedParticipatingPerson) => deletedParticipatingPerson !== participatingPersonKey
            )
            this.addParticipant(participatingPersonKey)
            this.lastRestoredParticipatingPersonKey = participatingPersonKey
        }
    },
    nuxtI18n: {
        paths: {
            de: '/anmeldung/:id/teilnehmer',
            en: '/registration/:id/participants' // -> accessible at /en/event
        }
    }
}
</script>

<style lang="scss" scoped>
.participants-grid {
    display: grid;
    gap: map-get($spacers, 2);
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    grid-auto-rows: minmax(150px, auto);

    @include media-breakpoint-down(xs) {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    }
}

.participants-deleted-grid {
    display: grid;
    gap: map-get($spacers, 2);
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    grid-auto-rows: minmax(150px, auto);
}

</style>
